import React from 'react';
import {createRoot} from 'react-dom/client';
import LearnAnswerSheetIsland from './components/LearnAnswerSheetIsland';
import './css/learn-page.css';

const GA_ID = 'G-HWREFSX6EM';
const ANSWER_CONTEXT_STORAGE_PREFIX = 'learnAnswerSheetContext:';
const ANSWER_CONTEXT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function readPageData() {
    try {
        const el = document.getElementById('quiz-article-page-data');
        if (!el || !el.textContent) return {};
        return JSON.parse(el.textContent) || {};
    } catch (e) {
        return {};
    }
}

function cleanUrl(slug) {
    if (typeof history === 'undefined' || !history.replaceState) return;
    if (!slug) return;
    const cleanPath = '/learn/' + encodeURIComponent(slug);
    try {
        history.replaceState({}, '', cleanPath);
    } catch (e) {
        // Some browsers may throw if origin mismatches — fail silently.
    }
}

function getAnswerContextStorageKey(slug) {
    return ANSWER_CONTEXT_STORAGE_PREFIX + encodeURIComponent(slug || '');
}

function getNow() {
    return Date.now ? Date.now() : new Date().getTime();
}

function isStoredContextValid(context, now) {
    return context && context.quizId && context.answerId && context.savedAt &&
        now - context.savedAt <= ANSWER_CONTEXT_TTL_MS;
}

function readStoredAnswerContext(slug) {
    try {
        if (!slug || typeof localStorage === 'undefined') return null;
        const parsed = JSON.parse(localStorage.getItem(getAnswerContextStorageKey(slug)) || '{}');
        return isStoredContextValid(parsed, getNow()) ? parsed : null;
    } catch (e) {
        return null;
    }
}

function writeStoredAnswerContext(slug, context) {
    try {
        if (!slug || typeof localStorage === 'undefined') return;
        if (!context) {
            localStorage.removeItem(getAnswerContextStorageKey(slug));
            return;
        }
        localStorage.setItem(getAnswerContextStorageKey(slug), JSON.stringify(context));
    } catch (e) {
        // Storage can fail in private browsing or when quota is full; article page still works.
    }
}

function saveAnswerContext(slug, quizId, answerId) {
    if (!slug || !quizId || !answerId) return null;
    const savedAt = getNow();
    const nextContext = {quizId, answerId, savedAt};
    writeStoredAnswerContext(slug, nextContext);
    return nextContext;
}

function getLatestStoredAnswerContext(slug) {
    const context = readStoredAnswerContext(slug);
    writeStoredAnswerContext(slug, context);
    return context;
}

function removeStoredAnswerContext(slug, answerId) {
    if (!slug || !answerId) return;
    const context = readStoredAnswerContext(slug);
    if (context && context.answerId === answerId) {
        writeStoredAnswerContext(slug, null);
    }
}

function initGoogleAnalytics() {
    // This function is called only after URL cleanup, so analytics receives the canonical page URL.
    if (!document.querySelector('script[data-analytics-loader="1"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.setAttribute('data-analytics-loader', '1');
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(GA_ID);
        document.head.appendChild(script);
    }
    if (typeof window.gtag !== 'function') {
        window.dataLayer = window.dataLayer || [];
        function gtag(){ window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
    }
    const cleanUrlStr = window.location.origin + window.location.pathname;
    window.gtag('config', GA_ID, {
        page_path: window.location.pathname,
        page_location: cleanUrlStr,
        page_title: document.title
    });
}

function injectAdsScript() {
    // Mirror src/form/index.js pattern. Inject the AdSense library once; ad units in the page
    // will pick it up automatically.
    if (document.querySelector('script[data-ads-loader="1"]')) return;
    const script = document.createElement('script');
    script.async = true;
    script.setAttribute('data-ads-loader', '1');
    script.crossOrigin = 'anonymous';
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2987317143669509';
    document.head.appendChild(script);
}

function showSubmittedCard(confirmationMessage) {
    const el = document.getElementById('learn-submitted');
    if (el) {
        el.classList.add('is-visible');
        el.removeAttribute('aria-hidden');
    }
    if (confirmationMessage) {
        const subtitle = document.getElementById('learn-submitted__subtitle');
        if (subtitle) subtitle.innerText = confirmationMessage;
    }
    // Reveal the share control in the compact submitted row. While the card is in the
    // submitting pre-state, CSS (.is-submitting .share-quiz) keeps it hidden.
    const share = document.getElementById('share-quiz');
    if (share) share.style.display = 'flex';
}

// Reveal the submitted card with the submitting animation: show a compact submitting
// state (green thin-ring spinner + "Submitting..."), then after ~3s cross-fade to the
// green tick + "Submitted" and let the centered CTA buttons appear. Used only on the real
// submit-reveal path (localStorage match), i.e. right after the player submitted this quiz.
function revealSubmittedWithAnimation(confirmationMessage) {
    const el = document.getElementById('learn-submitted');
    if (!el) {
        showSubmittedCard(confirmationMessage);
        return;
    }
    showSubmittedCard(confirmationMessage);
    // Already-answered responses are relabelled 'Answered' (and the icon swapped) by the
    // template's inline script, so they skip the Submitting -> Submitted animation.
    if (PAGE_DATA_JSON && PAGE_DATA_JSON.answered) return;
    const title = document.getElementById('learn-submitted__title');
    el.classList.add('is-submitting');
    if (title) title.innerText = 'Submitting...';
    window.clearTimeout(window.__learnSubmitTimer);
    window.__learnSubmitTimer = window.setTimeout(() => {
        el.classList.remove('is-submitting');
        if (title && title.innerText === 'Submitting...') {
            title.innerText = 'Submitted';
        }
    }, 3000);
}

function showSubmittedCardIfMatch(quizId, confirmationMessage, animate) {
    if (!quizId) return false;
    try {
        if (typeof localStorage === 'undefined') return false;
        const v = localStorage.getItem(quizId);
        if (!v) return false;
        // Play the Submitting to Submitted animation ONLY on a genuine fresh submit (answerId
        // present in the page data). On a refresh or a later visit we reveal the card instantly
        // from saved state, with no spinner.
        if (animate) {
            revealSubmittedWithAnimation(confirmationMessage);
        } else {
            showSubmittedCard(confirmationMessage);
        }
        return true;
    } catch (e) {
        return false;
    }
}

function hideLearnCtaButton(id) {
    const button = document.getElementById(id);
    if (!button) return;
    button.style.display = 'none';
    button.onclick = null;
}

function updateLearnQuizCtas({quizId, answerId, quizSetting, confirmationMessage, showSubmitted}) {
    if (showSubmitted) {
        showSubmittedCard(confirmationMessage);
    }

    const addAnotherAnswer = document.getElementById('add-another-answer');
    if (quizId && quizSetting && quizSetting.allow_multiple_submit && addAnotherAnswer) {
        addAnotherAnswer.style.display = 'flex';
        addAnotherAnswer.onclick = function () {
            window.location.href = '/id/' + encodeURIComponent(quizId);
        };
    } else {
        hideLearnCtaButton('add-another-answer');
    }

    const viewLeaderboard = document.getElementById('view-leaderboard');
    if (quizId && answerId && quizSetting && quizSetting.show_leaderboard && viewLeaderboard) {
        viewLeaderboard.style.display = 'flex';
        viewLeaderboard.onclick = function () {
            window.location.href = '/id/' + encodeURIComponent(quizId) + '#leaderboard?rid=' + encodeURIComponent(answerId);
        };
    } else {
        hideLearnCtaButton('view-leaderboard');
    }
}

function maybeMountAnswerSheetIsland(answerId, slug, shouldShowSubmittedOnLoad) {
    if (!answerId) return false;
    const root = document.getElementById('answer-sheet-root');
    if (!root) return false;
    createRoot(root).render(
        <LearnAnswerSheetIsland
            answerId={answerId}
            slug={slug}
            onAnswerSheetLoaded={({quizId, answerId: loadedAnswerId, quizSetting, confirmationMessage}) => {
                if (quizId && loadedAnswerId) {
                    saveAnswerContext(slug, quizId, loadedAnswerId);
                }
                updateLearnQuizCtas({
                    quizId,
                    answerId: loadedAnswerId || answerId,
                    quizSetting,
                    confirmationMessage,
                    showSubmitted: shouldShowSubmittedOnLoad
                });
            }}
            onAnswerSheetUnavailable={(unavailableAnswerId, shouldRemoveStoredContext) => {
                if (shouldRemoveStoredContext) {
                    removeStoredAnswerContext(slug, unavailableAnswerId);
                }
                moveTopAdToArticleOnlyPosition();
            }}
        />
    );
    return true;
}

// If neither Submitted card nor answer-sheet root has content, move the top ad below the H1.
// This keeps the article H1 above the fold for clean-Google-visit traffic.
function maybeSwapTopAdPosition(submittedVisible, answerSheetMounted) {
    if (submittedVisible || answerSheetMounted) return;
    const topAd = document.getElementById('learn-ad-top');
    const article = document.getElementById('learn-article');
    if (!topAd || !article) return;
    // Insert the ad after the share buttons (which sit under the byline) so the
    // article header — title, date, share — stays together above the ad.
    // Fall back to the first paragraph (the date), then to the article start.
    const anchor = article.querySelector('.learn-article__meta')
        || article.querySelector('.share-images')
        || article.querySelector('p');
    if (!anchor || !anchor.parentNode) {
        article.insertBefore(topAd, article.firstChild);
        return;
    }
    if (anchor.nextSibling) {
        anchor.parentNode.insertBefore(topAd, anchor.nextSibling);
    } else {
        anchor.parentNode.appendChild(topAd);
    }
    topAd.classList.add('learn-ad--inline');
}

function moveTopAdToArticleOnlyPosition() {
    maybeSwapTopAdPosition(false, false);
}

function initAdsUnits() {
    try {
        const units = document.querySelectorAll('.adsbygoogle');
        units.forEach(() => {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        });
    } catch (e) {
        console.error('AdSense initialization error:', e);
    }
}

function init() {
    const pageData = readPageData();
    const {quizId, answerId, slug} = pageData;
    const pageContext = quizId && answerId ? saveAnswerContext(slug, quizId, answerId) : null;
    let storedContext = answerId ? null : getLatestStoredAnswerContext(slug);
    if (quizId && storedContext && storedContext.quizId !== quizId) {
        storedContext = null;
    }
    const effectiveQuizId = quizId || (storedContext && storedContext.quizId);
    const effectiveAnswerId = answerId || (storedContext && storedContext.answerId);

    cleanUrl(slug);

    // GA + Ads happen AFTER URL cleanup so pageview / ad context records the clean URL.
    initGoogleAnalytics();

    // Ad shows for ALL users (paid + free) per founder, so it is not gated on showAds.
    injectAdsScript();

    // A genuine fresh submit is signalled by answerId present in the server-injected page data
    // (the ?answerid= the submit redirect carries). On a refresh, cleanUrl has stripped it, so
    // the card is revealed instantly with no submitting animation.
    const isFreshSubmit = Boolean(answerId);
    const submittedVisible = showSubmittedCardIfMatch(
        effectiveQuizId,
        pageData.quizSetting && pageData.quizSetting.confirmation_message,
        isFreshSubmit
    );

    updateLearnQuizCtas({
        quizId: effectiveQuizId,
        answerId: effectiveAnswerId,
        quizSetting: pageContext ? pageData.quizSetting : null,
        confirmationMessage: pageData.quizSetting && pageData.quizSetting.confirmation_message,
        showSubmitted: false
    });

    // Mount the React island; component itself handles fetch + decrypt + render.
    // We pre-decide whether to bother mounting based on presence of answerId.
    const islandMounted = maybeMountAnswerSheetIsland(effectiveAnswerId, slug, Boolean(storedContext) || submittedVisible);

    maybeSwapTopAdPosition(submittedVisible, islandMounted);

    // Ad shows for ALL users (paid + free) per founder, so it is not gated on showAds.
    initAdsUnits();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
